import { followLink, ctrlClickMac, ctrlClickWindows } from './ReactEditor.locstring.json';
import loc, { format } from 'owa-localize';
import { observer } from 'mobx-react';
import EditorViewState from '../store/schema/EditorViewState';
import { SmartOrderedListFeature } from '../utils/SmartOrderedListFeature';
import getDefaultComposeFormat from '../utils/getDefaultComposeFormat';
import { addContentOperator, removeContentOperator } from '../utils/ContentOperator';
import createPluginUtils from 'owa-editor-plugin-utils/lib/utils/createPluginUtils';
import isEditorPluginEx from 'owa-editor-plugin-utils/lib/utils/isEditorPluginEx';
import { transformElementForDarkMode } from 'owa-dark-mode-utilities';
import { isFeatureEnabled } from 'owa-feature-flags';
import ViewStateManagePlugin from '../internalPlugin/ViewStateManagePlugin';
import SelectImagePlugin from '../internalPlugin/SelectImagePlugin';
import createSnapshotsService from '../utils/createSnapshotsService';
import { isMac } from 'owa-user-agent/lib/userAgent';
import * as React from 'react';
import { Editor } from 'roosterjs-editor-core';
import { DefaultFormat, EditorOptions, EditorPlugin } from 'roosterjs-editor-types';
import { HtmlSanitizer, findClosestElementAncestor } from 'roosterjs-editor-dom';
import PluginUI from './PluginUI';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import updateContent from '../actions/updateContent';
import {
    ContentEdit,
    CustomReplace,
    CutPasteListChain,
    HyperLink,
    Paste,
    TableResize,
    Watermark,
} from 'owa-editor-lazy-internal-plugins';
import CtsWorkaroundPlugin from '../internalPlugin/CtsWorkaroundPlugin';
import { getPaletteAsRawColors } from 'owa-theme';

export interface ReactEditorProps {
    viewState: EditorViewState;
    plugins?: EditorPlugin[];
    classNames?: string;
    updateOnKeyPress?: boolean;
    updateOnDispose?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    watermark?: string;
    watermarkFormat?: DefaultFormat;
    ariaLabel?: string;
    isDarkMode?: boolean;
    scrollContainerSelector?: string;
    focusOnInit?: boolean;
    contentEditable?: boolean;
}

const styles = require('./ReactEditor.scss');
const classname = require('classnames/bind').bind(styles);
const WATERMARK_TEXT_FORMAT: DefaultFormat = {
    fontFamily:
        '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
    fontSize: '14px',
    textColor: '#666', // $ms-color-neutralSecondary
};

@observer
export default class ReactEditor extends React.Component<ReactEditorProps, {}> {
    private editor: Editor;
    private editorDiv: HTMLDivElement;

    componentDidMount() {
        this.editor = new Editor(this.editorDiv, this.getEditorOptions());
        this.updateContentToViewState(true /*isInitializing*/);

        if (this.props.focusOnInit) {
            this.editor.focus();
        }

        addContentOperator(
            this.props.viewState,
            this.editor /* htmlEditor */,
            null /* plainTextEditor */
        );
    }

    componentWillUnmount() {
        if (this.editor) {
            this.editor.dispose();
        }

        removeContentOperator(this.props.viewState, this.editor, null /*plainTextEditor*/);

        this.editor = null;
    }

    //tslint:disable-next-line:react-strict-mode  Tracked by WI 78449
    UNSAFE_componentWillReceiveProps(nextProps: ReactEditorProps) {
        // If being provided a new view state, reset the content
        if (nextProps.viewState !== this.props.viewState) {
            // When new viewState comes in we need to preserve the content of editor and update
            // the old viewState with content so that when the update content is updated the old content
            // won't be lost. After the old viewState is update, then take the content of the new viewState
            // sync it with the editor content.

            // Update the content of the editor into the old viewState to preserve the editted content
            this.updateContentToViewState(false /*isInitializing*/);
            // Sanitize the content in the new viewState and update the editor content
            let contentAfterCSSInlining = HtmlSanitizer.convertInlineCss(
                nextProps.viewState.content
            );
            this.editor.setContent(contentAfterCSSInlining, true /* triggerContentChangedEvent */);
            // Take the new editor content and update it to new viewState
            updateContent(nextProps.viewState, this.editor.getContent(), true /*isInitializing*/);
        }
        if (nextProps.isDarkMode !== this.props.isDarkMode) {
            this.setDarkMode(nextProps.isDarkMode);
        }
    }

    render() {
        const {
            onFocus,
            classNames,
            viewState,
            watermark,
            ariaLabel,
            isDarkMode,
            plugins,
            contentEditable,
        } = this.props;
        const isRtl = viewState.textDirection
            ? viewState.textDirection === 'rtl'
            : getUserConfiguration().SessionSettings.IsUserCultureRightToLeft;

        return (
            <>
                <div
                    /*
                        The contenteEditable prop has been temporarily added and should be removed once we switch towards
                        a better flow to create fluid components outside of editors.
                        WI: https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/96510
                    */
                    contentEditable={contentEditable ?? true}
                    ref={ref => (this.editorDiv = ref)}
                    dir={isRtl ? 'rtl' : 'ltr'}
                    className={classname(classNames, styles.textSelectable, {
                        resetColors: !isDarkMode,
                        anchorReset: isDarkMode,
                    })}
                    onBlur={this.onBlur}
                    onFocus={onFocus}
                    role="textbox"
                    aria-multiline="true"
                    aria-label={ariaLabel ? ariaLabel : watermark}
                />
                {/**
                 * Add an empty DIV to workaround https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/8885
                 */}
                <div />
                <PluginUI plugins={plugins} />
            </>
        );
    }

    public focus() {
        if (this.editor) {
            this.editor.focus();
        }
    }

    private updateContentToViewState(isInitializing?: boolean) {
        if (this.editor) {
            updateContent(this.props.viewState, this.editor.getContent(), isInitializing);
        }
    }

    private getEditorOptions() {
        const {
            plugins,
            viewState,
            watermark,
            watermarkFormat,
            isDarkMode,
            scrollContainerSelector,
            updateOnKeyPress,
            updateOnDispose,
        } = this.props;

        const enableExperimentFeatures = isFeatureEnabled('cmp-editor-experimentFeatures');
        const defaultFormat = getDefaultComposeFormat(isDarkMode);
        const markdownEnabled = isFeatureEnabled('cmp-markdownInline');
        const clickableLinkTooltip = format(
            loc(followLink),
            isMac() ? loc(ctrlClickMac) : loc(ctrlClickWindows)
        );

        const allPlugins = [
            ...(plugins || []),
            new ContentEdit(
                {
                    mergeInNewLineWhenBackspaceOnFirstChar: true,
                    markdownBold: markdownEnabled,
                    markdownItalic: markdownEnabled,
                    markdownStrikethru: markdownEnabled,
                    markdownInlineCode: markdownEnabled,
                },
                enableExperimentFeatures ? [] : [SmartOrderedListFeature]
            ),
            new HyperLink(href => clickableLinkTooltip + '\r\n' + href),
            new Paste(),
            new TableResize(),
            new CustomReplace(),
            new CutPasteListChain(),
            watermark && new Watermark(watermark, watermarkFormat || WATERMARK_TEXT_FORMAT),
            new SelectImagePlugin(viewState),
            new ViewStateManagePlugin(viewState, updateOnKeyPress, updateOnDispose),
            CtsWorkaroundPlugin,
        ];

        const pluginUtils = createPluginUtils(this.editorDiv);
        allPlugins.forEach(
            plugin => isEditorPluginEx(plugin) && plugin.setPluginUtils(pluginUtils)
        );

        const initialContent = HtmlSanitizer.convertInlineCss(viewState.content);
        const options: EditorOptions = {
            plugins: allPlugins,
            defaultFormat: defaultFormat,
            undoSnapshotService: createSnapshotsService(viewState),
            initialContent: initialContent,
            inDarkMode: isDarkMode,
            enableExperimentFeatures,
            allowKeyboardEventPropagation: true,
            scrollContainer:
                scrollContainerSelector &&
                findClosestElementAncestor(
                    this.editorDiv,
                    this.editorDiv.ownerDocument.body,
                    scrollContainerSelector
                ),
            onExternalContentTransform: element =>
                transformElementForDarkMode(
                    element,
                    getPaletteAsRawColors().white,
                    true /* useSimpleMethod */
                ),
        };

        return options;
    }

    private onBlur = () => {
        this.updateContentToViewState();
        if (this.props.onBlur) {
            this.props.onBlur();
        }
    };

    private setDarkMode(nextDarkMode: boolean) {
        this.editor.setDarkModeState(nextDarkMode);
    }
}
